import { BasketProductFullParams } from 'services/basket/interfaces';
import { formatProducts, handlePushEvent, buildUserData } from '../helpers';
import {
	BannerProps,
	BoniInfoProps,
	CheckoutPageLoadProps,
	CloseOrderProps,
	onSeeMoreProps,
	PaymentFormChangeProps,
	SideBasketOpenProps,
	ViewBoniPointsAmountProps,
	ViewBoniPointsProps,
} from './interfaces';
import { buildBoniInfoEvent, mapBoniGtmItem, mapBoniProductItem } from './helpers';

const currency = 'BRL';

export const handleParseProductList = (products: BasketProductFullParams[][], additionalItems = false) => {
	const formattedProducts = formatProducts(products, additionalItems);
	const items = Array.isArray(products) ? formattedProducts : [];

	return { items };
};

export function onPaymentFormChange({
	paymentFormLabel,
	paymentFormBasketsData,
	totalPricePromotion,
}: PaymentFormChangeProps['params']) {
	const event: PaymentFormChangeProps['event'] = {
		value: totalPricePromotion.toFixed(2),
		currency,
		ecommerce: handleParseProductList(paymentFormBasketsData),
		payment_type: paymentFormLabel,
	};

	handlePushEvent('add_payment_info', event);
}

export function onSideBasketOpen({ basketProductsData, totalPricePromotion }: SideBasketOpenProps['params']) {
	const event: SideBasketOpenProps['event'] = {
		value: totalPricePromotion.toFixed(2),
		currency,
		ecommerce: handleParseProductList(basketProductsData),
	};

	handlePushEvent('view_cart', event);
}

export function onCheckoutPageLoad({
	basketProductsData,
	totalPricePromotion,
	user_engaged_boni,
}: CheckoutPageLoadProps['params']) {
	const event: CheckoutPageLoadProps['event'] = {
		value: totalPricePromotion.toFixed(2),
		currency,
		ecommerce: handleParseProductList(basketProductsData),
		user_engaged_boni: user_engaged_boni,
	};

	handlePushEvent('begin_checkout', event);
}

export function onCloseOrder({
	posCNPJ,
	posName,
	hasCombo,
	isEngagedBoni,
	totalBoniPoints,
	basketGroupCode,
	distributorsName,
	totalPricePromotion,
	totalTaxSubstitution,
	closedOrderBasketsData,
	customerName,
	customerEmail,
	customerPhoneNumber,
	customerPhoneConfirmed,
	posStreet,
	posCity,
	posRegion,
	posPostalCode,
}: CloseOrderProps['params']) {
	const event: CloseOrderProps['event'] = {
		tax: totalTaxSubstitution,
		value: totalPricePromotion.toFixed(2),
		currency,
		ecommerce: handleParseProductList(closedOrderBasketsData),
		has_combo: hasCombo,
		distributors: distributorsName,
		pos_cnpj: posCNPJ,
		pos_name: posName,
		transaction_id: basketGroupCode,
		user_engaged_boni: isEngagedBoni,
		boni_points_amount: totalBoniPoints,
		userData: buildUserData({
			name: customerName,
			email: customerEmail,
			phoneNumber: customerPhoneNumber,
			phoneNumberConfirmed: customerPhoneConfirmed,
			street: posStreet,
			city: posCity,
			region: posRegion,
			postalCode: posPostalCode,
		}),
	};
	handlePushEvent('purchase', event);
}

function createBannerBoniEvent(bannerId: string, origin?: string): BannerProps {
	return {
		banner_id: bannerId,
		campaign_name: 'Boni',
		banner_position: 'top',
		...(origin && { origin }),
	};
}

export function onBannerBoniView(bannerId: string) {
	handlePushEvent('banner_impression', createBannerBoniEvent(bannerId));
}

export function onBannerBoniClick(bannerId: string, origin: string) {
	handlePushEvent('banner_click', createBannerBoniEvent(bannerId, origin));
}

export function onSeeMoreClick(params: onSeeMoreProps['params']) {
	handlePushEvent('click_boni_info', buildBoniInfoEvent(params));
}

export function seeBoniInfo({ page, userType, ...rest }: BoniInfoProps['params']) {
	handlePushEvent('view_boni_points', {
		...buildBoniInfoEvent(rest),
		page_name: page,
		user_type: userType,
	});
}

export const updateCartGTMWhithBoni = (
	type: string,
	quantity: number,
	products: any,
	quantityPrevious?: number,
	cartValue?: number,
	format?: boolean,
) => {
	const items = format
		? Array.isArray(products)
			? formatProducts(products, false)
			: []
		: Array.isArray(products)
		? products.map((product) => mapBoniGtmItem(product, type))
		: [];

	const eventData = { items };

	handlePushEvent('change_quantity_after_boni_view', {
		currency: 'BRL',
		ecommerce: eventData,
		cart_value: cartValue,
		quantity_new: quantity,
		quantity_previous: quantityPrevious,
	});

	return eventData;
};

export const updateCartBoniPoints = (
	quantity: number,
	cartValue?: number,
	previousPoints?: number,
	currentPoints?: number,
	products?: any,
) => {
	const items = Array.isArray(products) ? products.map((p) => mapBoniGtmItem(p)) : [];
	const eventData = { items };

	handlePushEvent('update_product_boni_points', {
		currency: 'BRL',
		quantity,
		ecommerce: eventData,
		cart_value: cartValue,
		current_points: currentPoints,
		previous_points: previousPoints,
	});

	return eventData;
};

export function onViewBoniPoints({
	levelName,
	basketProductsData,
	totalPricePromotion,
}: ViewBoniPointsProps['params']) {
	const items = basketProductsData.flat().flatMap((product) => {
		if (product.productType === 'COMBO') {
			return product.productsInCombo.map((item) =>
				mapBoniProductItem({
					price: item.unitPricePromotion,
					ean13: item.ean13,
					isCombo: true,
					quantity: item.quantity,
					discount: item.discount,
					levelName,
					promotionId: product.hyperaCode,
					affiliation: product.affiliation,
					description: item.description,
					industryName: product.industryName,
					itemListName: product.suggestionGroupName,
					promotionName: product.description,
					businessUnitName: product.businessUnitName,
				}),
			);
		}

		return [
			mapBoniProductItem({
				ean13: product.ean13,
				price: product.unitPricePromotion,
				isCombo: false,
				quantity: product.quantity,
				discount: product.unitDiscount,
				levelName,
				affiliation: product.affiliation,
				description: product.description,
				industryName: product.industryName,
				itemListName: product.suggestionGroupName,
				businessUnitName: product.businessUnitName,
			}),
		];
	});

	const event: ViewBoniPointsProps['event'] = {
		value: totalPricePromotion.toFixed(2),
		currency,
		ecommerce: { items },
	};

	handlePushEvent('view_product_boni_points', event);
}

export function onViewBoniPointsAmount({
	cartValue,
	itemsCount,
	changeSource,
	currentPoints,
	previousPoints,
}: ViewBoniPointsAmountProps['params']) {
	const event: ViewBoniPointsAmountProps['event'] = {
		cart_value: cartValue,
		items_count: itemsCount,
		change_source: changeSource,
		current_points: currentPoints,
		previous_points: previousPoints,
	};

	handlePushEvent('view_boni_points_amount', event);
}
